import { selectUserInfo } from "features/user/userSlice";
import { Grid, TextField } from "@mui/material";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { PriceListProfileService } from "subModule/src/core/services";
import AlertMessages from "common/Alert/AlertMessages";
import { useSnackbar } from "notistack";
import SimpleModal from "common/Form/Form";
import { useNavigate, useParams } from "react-router-dom";
import ComponentHeaderTwo from "common/ComponentHeaderTwo/ComponentHeaderTwo";
import { ReactComponent as Pricelist } from "assets/SideBarIcons/price-tag.svg";
import { checkPrivileges } from "Helpers/CheckPrivileges";
import { useBusinessSetupEntityBreadcrumbs } from "Helpers/useBusinessSetupEntityBreadcrumbs";
import { useEffect, useState } from "react";
import { GetPricelistByIdResponse } from "subModule/src/services/pricelist/interface";
import Loader from "layout/Loader";
import PricelistTabs from "./PricelistTabs";
// import CommonCheckbox from "common/CheckBox/Checkbox";
// import BasicDatePicker from "common/Pickers/Pickers";
// import { themes } from "theme/themes";
// import { brandingInfo } from "features/server/brandingSlice";

interface FormData {
  [key: string]: any;
}
export default function PricelistsModal() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const params = useParams();
  const { enqueueSnackbar } = useSnackbar();
  // const branding = useSelector(brandingInfo);
  // const themeColors = branding?.attributes?.themeId === 100 ? branding?.attributes?.customTheme : (themes as any)[branding?.attributes?.themeId || 3];
  const {
    register,
    handleSubmit,
    formState: { errors, dirtyFields, isDirty },
    reset,
  } = useForm<FormData>({ defaultValues: {} });
  const loggedInUserData = useSelector(selectUserInfo);
  const primaryLanguage = loggedInUserData?.business?.primaryLanguage;
  const secondaryLanguage = loggedInUserData?.business?.secondaryLanguage;
  const isArabicLanguage = (lang?: string) => {
    const l = (lang || "").toLowerCase();
    return l === "arabic" || l === "ar" || l.includes("arab");
  };
  const localizedPriceListPlaceholder = (lang?: string) =>
    isArabicLanguage(lang) ? t("phPriceListAr") : t("phPriceListEn");
  const [loading, setLoading] = useState<boolean>(params?.id ? true : false);
  const [IsBtnEnabled, setIsBtnEnabled] = useState<boolean>(
    params?.id ? false : true,
  );
  const { breadcrumbItems, captureLabel } = useBusinessSetupEntityBreadcrumbs({
    sectionLabelKey: "Items",
    moduleLabelKey: "priceLists",
    moduleHref: "/price-lists",
    entityFallbackLabelKey: "title",
    isEdit: !!params?.id,
  });
  useEffect(() => {
    if (params?.id) {
      if (loading) getByID();
    }
  }, [params]);
  useEffect(() => {
    if (isDirty && params?.id) setIsBtnEnabled(true);
  }, [isDirty, dirtyFields]);
  const getByID = () => {
    PriceListProfileService.getPricelistById(+(params as any)?.id)
      .then((res: GetPricelistByIdResponse) => {
        if (res.success) {
          reset({ ...(res?.data || {}) });
          captureLabel(res?.data?.title);
          setLoading(false);
        } else {
          enqueueSnackbar(
            <AlertMessages text={res.message || "Something went wrong"} />,
            { variant: "error" },
          );
        }
      })
      .catch((err: any) => {
        console.error("===", err);
      });
  };
  const onSubmit = (data: any) => {
    setIsBtnEnabled(false);
    if (params?.id) {
      PriceListProfileService.updatePriceList(+params?.id, { ...data })
        .then((response: any) => {
          if (response.success) {
            captureLabel(data?.title);
            getByID();
            enqueueSnackbar(
              <AlertMessages
                statusCode={response.status}
                text={
                  t("priceList") + " " + t("updated") + " " + t("successfully")
                }
              />,
              { variant: "success" },
            );
          } else {
            enqueueSnackbar(<AlertMessages text={response?.message} />, {
              variant: "error",
            });
            setIsBtnEnabled(true);
          }
        })
        .catch((err: any) => {
          console.error("===", err);
          setIsBtnEnabled(true);
          enqueueSnackbar(
            <AlertMessages
              text={
                err?.message || err?.data?.message || "Something went wrong"
              }
            />,
            { variant: "error" },
          );
        });
    } else {
      PriceListProfileService.createPriceList(data)
        .then((response: any) => {
          if (response.success) {
            enqueueSnackbar(
              <AlertMessages
                statusCode={response.status}
                text={
                  t("priceList") + " " + t("created") + " " + t("successfully")
                }
              />,
              { variant: "success" },
            );
            navigate("/price-lists");
          } else {
            setIsBtnEnabled(true);
            enqueueSnackbar(<AlertMessages text={response?.message} />, {
              variant: "error",
            });
          }
        })
        .catch((err: any) => {
          console.error("===", err);
          setIsBtnEnabled(true);
          enqueueSnackbar(
            <AlertMessages
              text={
                err?.message || err?.data?.message || "Something went wrong"
              }
            />,
            { variant: "error" },
          );
        });
    }
  };

  return (
    <>
      <ComponentHeaderTwo
        showBtn={true}
        showSaveBtn={true}
        onClick={() => navigate("/price-lists")}
        onSubmit={handleSubmit(onSubmit)}
        heading={params?.id ? t("priceLists") : t("addPriceLists")}
        icon={<Pricelist height={28} width={28} />}
        btnText={"back"}
        btnType={"back"}
        isBtnEnabled={IsBtnEnabled}
        showCreateBtn={checkPrivileges("ROLE_PRICELIST_CREATE")}
        breadcrumbItems={breadcrumbItems}
      />
      <div className={`${params?.id ? "flex" : ""}`}>
        {params?.id ? (
          <PricelistTabs params={params} selectedIndex={0} />
        ) : null}
        {loading ? (
          <Loader />
        ) : (
          <SimpleModal
            visable={true}
            title={params?.id ? t("priceLists") : t("addPriceLists")}
            isButtonVisable={true}
            isVisableBtn={IsBtnEnabled}
            btnclosetext="Back"
            buttonText={params?.id ? "update" : "submit"}
            attBtnNotshow={false}
            notVisableBackbtn={true}
            showPortList={handleSubmit(onSubmit)}
            formSubmit={handleSubmit(onSubmit)}
            content={
              <Grid
                container
                component="form"
                id="form"
                spacing={3}
                onSubmit={handleSubmit(onSubmit)}
              >
                <Grid item xs={12} md={3}>
                  <TextField
                    {...register("title", {
                      validate: {
                        required: (value) =>
                          value.trim() !== "" || t("This field is required."),
                      },
                    })}
                    label={`${!primaryLanguage ? t("primary") : ""} ${t("title")} ${primaryLanguage ? ` (${t(primaryLanguage)})` : ""} *`}
                    placeholder={localizedPriceListPlaceholder(
                      primaryLanguage,
                    )}
                    title={`${!primaryLanguage ? t("primary") : ""} ${t("title")} ${primaryLanguage ? ` (${t(primaryLanguage)})` : ""} *`
                      .replace(/\s+/g, " ")
                      .trim()}
                    fullWidth
                    error={!!errors.title}
                    helperText={errors.title?.message as string}
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
                <Grid item xs={12} md={3}>
                  <TextField
                    {...register("secondaryTitle")}
                    label={`${!secondaryLanguage ? t("secondary") : ""} ${t("title")} ${secondaryLanguage ? ` (${t(secondaryLanguage)})` : ""}`}
                    placeholder={localizedPriceListPlaceholder(
                      secondaryLanguage,
                    )}
                    title={`${!secondaryLanguage ? t("secondary") : ""} ${t("title")} ${secondaryLanguage ? ` (${t(secondaryLanguage)})` : ""}`
                      .replace(/\s+/g, " ")
                      .trim()}
                    fullWidth
                    error={!!errors.secondaryTitle}
                    helperText={errors.secondaryTitle?.message as string}
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
                {/* <Grid container spacing={2} className='my-[0px] ml-[8px]'>
                            <Grid item xs={12} md={3}>
                                <Controller
                                    name="haveExpiry"
                                    control={control}
                                    defaultValue={false}
                                    render={({ field }) => (
                                        <CommonCheckbox
                                            {...field}
                                            label={t("haveExpiry")}
                                            onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                                                field.onChange(event.target.checked);
                                                if(!event.target.checked) setValue('endDate', '');
                                            }}
                                            checked={field.value}
                                            color={themeColors?.primary}
                                        />
                                    )}
                                />
                            </Grid>
                            <Grid item xs={12} md={3}>
                                <Controller
                                    name="startDate"
                                    control={control}
                                    rules={{ validate: (value) => {
                                        const date = new Date(value);
                                        return isNaN(date.getTime()) ? t("Invalid Date") : true;
                                    } }}
                                    render={({ field }) => (
                                        <BasicDatePicker
                                            type="datePicker"
                                            {...field}
                                            label={t('selectStartDate') + ' *'}
                                            fullWidth
                                            onChange={(value: any) => {field.onChange(new Date(value).toLocaleDateString('en-CA', { year: 'numeric', month: '2-digit', day: '2-digit' })?.split('/').reverse().join('-')); trigger('startDate');}}
                                            required={false}
                                            error={errors.startDate?.message as string}
                                        />
                                    )}
                                />
                            </Grid>
                            {watch('haveExpiry') ? <Grid item xs={12} md={3}>
                                <Controller
                                    name="endDate"
                                    control={control}
                                    rules={{ validate: (value) => {
                                        if (!watch('haveExpiry')) return true;
                                        const date = new Date(value);
                                        if (isNaN(date.getTime())) return t("Invalid Date");
                                        const startDate = new Date(getValues("startDate"));
                                        if (startDate && date <= startDate) return t("endDateOrder");
                                        return true;
                                    } }}
                                    render={({ field }) => (
                                        <BasicDatePicker
                                            type="datePicker"
                                            {...field}
                                            label={t('selectEndDate') + (watch('haveExpiry') ? ' *' : '')}
                                            fullWidth
                                            onChange={(value: any) => {field.onChange(new Date(value).toLocaleDateString('en-CA', { year: 'numeric', month: '2-digit', day: '2-digit' })?.split('/').reverse().join('-')); trigger('endDate');}}
                                            required={false}
                                            error={errors.endDate?.message as string}
                                            disable={watch('haveExpiry') === false}
                                        />
                                    )}
                                />
                            </Grid> : null}
                        </Grid> */}
              </Grid>
            }
          />
        )}
      </div>
    </>
  );
}
